import type { Cargo } from '../../../../redux/reducers/cargos'
import type { Client } from '../../../../redux/reducers/clients'
import type {
  GroupedCargoClientRow,
  ReportCargoDirection,
  ReportGroupedCargoClient,
  ReportGroupedCargoItem,
} from '../../../../redux/types/reportGroupedCargos'
import type { PickupSenderDef, ShipmentRow } from './cargoTypes'
import { buildYandexMapsHrefFromClient } from './clientMapUrl'
import {
  parseCargoIsPaid,
  parseCargoReadyForPickup,
} from './cargoReadinessDate'

/** Показ кнопки удаления груза в списке путевого: константа или правило по строке API */
export type ShowDeleteForShipment =
  | boolean
  | ((item: ReportGroupedCargoItem) => boolean)

export function resolveShowDeleteForShipment(
  flag: ShowDeleteForShipment,
  item: ReportGroupedCargoItem
): boolean {
  return typeof flag === 'function' ? flag(item) : flag
}

const placeholderClient = { id: 0, name: '' } as Client

/** Синтетическая карточка «без получателя» в разгрузке (не участвует в PATCH доставки / порядке клиентов) */
export function unloadingNoRecipientRowKey(
  direction: ReportCargoDirection
): string {
  return `grp-${direction}-unloading-no-recipient`
}

export function isUnloadingNoRecipientSenderRowKey(
  rowKey: string,
  direction: ReportCargoDirection
): boolean {
  return rowKey === unloadingNoRecipientRowKey(direction)
}

/** Нет получателя у груза: пустой to_id или снят объект получателя на бэке */
export function isGroupedCargoMissingUnloadingRecipient(
  item: ReportGroupedCargoItem
): boolean {
  const tid = item.to_id
  if (tid == null || Number(tid) <= 0) return true
  if (item.to == null) return true
  if (Number(item.to.id) <= 0) return true
  return false
}

function clientDisplayName(
  client?: { entity_name?: string; name?: string } | null
): string {
  if (!client) return ''
  return (client.entity_name || client.name || '').trim()
}

/** Загрузка — получатель с ⊳ сзади; разгрузка — отправитель с ⊳ спереди */
export function cargoCounterpartyListLabel(
  listKind: 'loading' | 'unloading',
  cargo?: Pick<Cargo, 'from' | 'to' | 'to_id'> | null
): string | null {
  if (!cargo) return null
  if (listKind === 'loading') {
    const tid = cargo.to_id
    if (tid == null || Number(tid) <= 0) return null
    const toName = clientDisplayName(cargo.to)
    return toName ? `⊳ ${toName}` : null
  }
  const fromName = clientDisplayName(cargo.from)
  return fromName ? `${fromName} ⊳` : null
}

export function displayNameFromGroupedClient(
  client: ReportGroupedCargoClient
): string {
  const entity = client.entity_name?.trim()
  const nm = client.name?.trim()
  const label = entity || nm || ''
  if (label) return label
  if (client.id > 0) return `Клиент №${client.id}`
  return 'Без получателя'
}

/**
 * Строка группировки с бэка без `client` (часто при отсутствии получателя):
 * строим заголовок и id из первого груза, чтобы не падать на `row.client.id`.
 */
export function resolveGroupedRowClient(
  row: GroupedCargoClientRow,
  rowIndex: number
): ReportGroupedCargoClient {
  if (row.client != null) return row.client

  const item = row.cargos[0]
  if (!item) {
    return { id: -(rowIndex + 1), name: '', entity_name: 'Нет данных' }
  }

  const payer = item.payer ?? 0
  let resolvedId = 0
  let anchor: Client | undefined

  if (item.third_id != null && item.third_id > 0) {
    resolvedId = item.third_id
    anchor = item.client
  } else if (payer === 0) {
    resolvedId = item.from_id ?? 0
    anchor = item.from
  } else if (payer === 1) {
    resolvedId = item.to_id ?? 0
    anchor = item.to
  } else {
    resolvedId = item.third_id ?? item.from_id ?? item.to_id ?? 0
    anchor = item.client ?? item.from ?? item.to
  }

  const nameFromAnchor =
    anchor?.entity_name?.trim() || anchor?.name?.trim() || ''

  if (!resolvedId || resolvedId <= 0) {
    resolvedId = -(10000 + rowIndex)
  }

  const title =
    nameFromAnchor ||
    (resolvedId > 0 ? `Клиент №${resolvedId}` : 'Без получателя')

  return {
    id: resolvedId,
    name: anchor?.name ?? '',
    entity_name: title,
    phones: anchor?.phones,
  }
}

function resolveGroupedCargoDirection(
  item: ReportGroupedCargoItem,
  listDirection?: ReportCargoDirection
): ReportCargoDirection | undefined {
  if (item.direction === 'forward' || item.direction === 'backward') {
    return item.direction
  }
  if (listDirection === 'forward' || listDirection === 'backward') {
    return listDirection
  }
  return undefined
}

export function mapGroupedItemToCargo(
  item: ReportGroupedCargoItem,
  reportId: number,
  listDirection?: ReportCargoDirection
): Cargo {
  const from = (item.from ?? item.client ?? placeholderClient) as Client
  const to = (item.to ?? placeholderClient) as Client
  const direction = resolveGroupedCargoDirection(item, listDirection)

  return {
    id: item.id,
    report_id: item.report_id ?? reportId,
    invoice_id: item.invoice_id ?? null,
    from_id: item.from_id ?? from.id ?? 0,
    to_id: item.to_id ?? to.id ?? 0,
    payer: item.payer ?? 0,
    description: item.description ?? '',
    volume: item.volume ?? 0,
    weight: item.weight ?? 0,
    amount: item.amount ?? 0,
    currency: item.currency ?? '',
    has_tn: item.has_tn ?? false,
    tn_number: item.tn_number ?? '',
    third_id: item.third_id,
    client: item.client,
    from,
    to,
    payment_method: item.payment_method,
    is_paid: parseCargoIsPaid(item.is_paid),
    status: item.status,
    is_active: item.is_active,
    images: item.images,
    created_by: item.created_by ?? null,
    readiness_at: item.readiness_at ?? null,
    can_be_picked_up: parseCargoReadyForPickup(item.can_be_picked_up),
    ...(direction ? { direction } : {}),
  } as Cargo
}

export function groupedItemToShipmentRow(
  item: ReportGroupedCargoItem,
  reportId: number,
  showDelete: ShowDeleteForShipment,
  listDirection?: ReportCargoDirection
): ShipmentRow {
  return {
    id: String(item.id),
    title: item.description?.trim() ? item.description : `Груз №${item.id}`,
    colMain: 'col col-sm-10 pl-1',
    colRight: 'col-auto col-sm-2 pl-1 pr-0',
    showDelete: resolveShowDeleteForShipment(showDelete, item),
    showPhoto: (item.images?.length ?? 0) > 0,
    photoCount: item.images?.length,
    sourceCargo: mapGroupedItemToCargo(item, reportId, listDirection),
  }
}

export function buildSendersFromGroupedRows(
  rows: GroupedCargoClientRow[] | undefined,
  direction: ReportCargoDirection,
  listKind: 'loading' | 'unloading',
  reportId: number,
  showDeleteForShipment: ShowDeleteForShipment
): PickupSenderDef[] {
  if (!rows?.length) return []

  return rows.map((row, rowIndex) => {
    const c = resolveGroupedRowClient(row, rowIndex) as Client
    const cid = c.id
    const rowKey = `grp-${direction}-${listKind}-${cid}`
    return {
      rowKey,
      name: displayNameFromGroupedClient(c),
      count: row.cargos.length,
      listId: rowKey,
      shipments: row.cargos.map((cg) =>
        groupedItemToShipmentRow(cg, reportId, showDeleteForShipment, direction)
      ),
      clientStoreKey: String(cid),
      ...(c.phones?.length ? { draftPhones: c.phones } : {}),
      draftMapHref: buildYandexMapsHrefFromClient(c),
    }
  })
}

/**
 * Разгрузка: грузы без получателя (в т.ч. после снятия to) — в отдельной карточке внизу;
 * остальные — как в API, с обычной группировкой.
 */
export function buildUnloadingSendersFromGrouped(
  rows: GroupedCargoClientRow[] | undefined,
  direction: ReportCargoDirection,
  reportId: number,
  showDeleteForShipment: ShowDeleteForShipment
): {
  sortableSenders: PickupSenderDef[]
  noRecipientSender: PickupSenderDef | null
} {
  const rowsIn = rows ?? []
  const noRecipientCargos: ReportGroupedCargoItem[] = []
  const normalRows: GroupedCargoClientRow[] = []

  for (const row of rowsIn) {
    const stays: ReportGroupedCargoItem[] = []
    for (const cg of row.cargos) {
      if (isGroupedCargoMissingUnloadingRecipient(cg)) {
        noRecipientCargos.push(cg)
      } else {
        stays.push(cg)
      }
    }
    if (stays.length > 0) {
      normalRows.push({ client: row.client, cargos: stays })
    }
  }

  const sortableSenders = buildSendersFromGroupedRows(
    normalRows,
    direction,
    'unloading',
    reportId,
    showDeleteForShipment
  )

  if (!noRecipientCargos.length) {
    return { sortableSenders, noRecipientSender: null }
  }

  const rowKey = unloadingNoRecipientRowKey(direction)
  const noRecipientSender: PickupSenderDef = {
    rowKey,
    name: '-Без получателя-',
    listId: rowKey,
    count: noRecipientCargos.length,
    shipments: noRecipientCargos.map((cg) =>
      groupedItemToShipmentRow(cg, reportId, showDeleteForShipment, direction)
    ),
    hideDeliveredChecklist: true,
    hideHeaderContactActions: true,
  }

  return { sortableSenders, noRecipientSender }
}

export function clientIdsForPersistOrder(senders: PickupSenderDef[]): number[] {
  return senders
    .filter((s) => !s.rowKey.startsWith('pickup-added-'))
    .map((s) => Number(s.clientStoreKey))
    .filter((id) => Number.isFinite(id) && id > 0)
}

/** Карточки с отмеченным «все грузы» уходят вниз списка; порядок внутри групп сохраняется */
export function reorderSendersWithCompletedAtBottom(
  senders: PickupSenderDef[],
  checked: Record<string, boolean>
): PickupSenderDef[] {
  const incomplete = senders.filter((s) => !checked[s.rowKey])
  const complete = senders.filter((s) => !!checked[s.rowKey])
  return [...incomplete, ...complete]
}

export function sendersRowOrderChanged(
  a: PickupSenderDef[],
  b: PickupSenderDef[]
): boolean {
  if (a.length !== b.length) return true
  return a.some((s, i) => s.rowKey !== b[i].rowKey)
}
